import { 
  format, getTime, formatDistanceToNow, 
  startOfDay, addDays, addYears, isBefore, 
  isAfter, parseISO, isValid, differenceInDays,
  isLastDayOfMonth,
  isSameDay,
  endOfMonth,
  startOfMonth,
  differenceInMonths,
  addMonths,
  getDaysInMonth,
  subMonths,
  differenceInCalendarMonths,
  getDate,
  compareAsc,
  isSameMonth,
  subDays,
} from 'date-fns';


export const initialDate = {
  NEW_DAYS: new Date(), // 今天
  ADD_DAYS: addDays(new Date(), 1), // 明天
  SUB_DAYS: subDays(new Date(), 1), // 昨天
  NEW_MONTHS: addMonths(new Date(), 1), // 從今天起往後延伸一個月
  ADD_MONTHS: addMonths(addDays(new Date(), 1), 1), // 從明天起往後延伸一個月
};


// ==============================|| DATA FORMAT ||============================== //

export function fDate(date, newFormat) {
  const fm = newFormat || 'dd MMM yyyy';
  if (!date || Number.isNaN(new Date(date).getTime())) {
    return ''; // 返回空字符串表示無效日期
  }
  return date ? format(new Date(date), fm) : '';
}

export function fDateTime(date, newFormat) {
  const fm = newFormat || 'dd MMM yyyy p';

  return date ? format(new Date(date), fm) : '';
}

export function fTimestamp(date) {
  return date ? getTime(new Date(date)) : '';
}

export function fToNow(date) {
  return date
    ? formatDistanceToNow(new Date(date), {
        addSuffix: true,
      })
    : '';
}

export function fDateYear(date, newFormat) {
    const fm = newFormat || 'yyyy';

    return date ? format(new Date(date), fm) : '';
}



/* **************************************************************************************** */ 


// 禁用日期函数（使用 date-fns）
export function disabledDate(current) {
  if (!current) return false;
  
  const today = startOfDay(new Date()); // 今天的開始時間
  const tomorrow = addDays(today, 1); // 明天的日期
  const twoYearsLater = addYears(today, 2); // 2 年後的日期
  return isBefore(current.toDate(), today) || isAfter(current.toDate(), twoYearsLater);
};


/**
* 获取某个日期两年后的日期
* @param {Date|string} inputDate - 输入日期
* @returns {Date} 两年后的日期
*/
export function getDateTwoYearsLater(inputDate) {
  // 转换为Date对象
  const date = new Date(inputDate);
  // 验证输入
  if (Number.isNaN(date)) {
    console.warn('Invalid date format');
    return new Date();
  }
  // 加两年
  return addYears(date, 2);
}



/**
 * 使用 date-fns 計算兩個日期之間的天數
 * @param {string|Date} startDate - 開始日期
 * @param {string|Date} endDate - 結束日期
 * @returns {number} - 返回兩個日期之間的天數，若天數小於一天則返回 1
 */
export function fDateBetweenInDays(startDate, endDate) {
  // 確保日期是有效的 Date 對象
  const start = isValid(parseISO(startDate)) ? parseISO(startDate) : new Date(startDate);
  const end = isValid(parseISO(endDate)) ? parseISO(endDate) : new Date(endDate);

  // 如果日期無效，返回 1
  if (!isValid(start) || !isValid(end)) {
    console.warn('Invalid date(s) provided to betweenInDays.');
    return 1;
  }

  // 使用 differenceInDays 計算天數差
  const dayDiff = differenceInDays(end, start) + 1;

  // 確保天數至少為 1
  return dayDiff > 0 ? dayDiff : 1;
};


/**
 * 計算兩個日期之間的天數
 * @param {string|Date} startDate - 開始日期
 * @param {string|Date} endDate - 結束日期
 * @returns {number} - 返回兩個日期之間的天數，若天數小於一天則返回 1
 */
export function betweenInDays(startDate, endDate) {
  // 確保日期是有效的 Date 對象
  const start = new Date(startDate);
  const end = new Date(endDate);

  // 如果日期無效，返回 1
  if (Number.isNaN(start?.getTime()) || Number.isNaN(end?.getTime())) {
      console.warn('Invalid date(s) provided to betweenInDays.');
      return 1;
  }

  // 計算天數差
  const timeDiff = end.getTime() - start.getTime();
  const dayDiff = Math.ceil(timeDiff / (1000 * 60 * 60 * 24));

  // 確保天數至少為 1
  return dayDiff > 0 ? dayDiff : 1;
};




/**
* 判断两个日期哪个是最新日期
* @param {Date|string} date1 - 第一个日期
* @param {Date|string} date2 - 第二个日期
* @returns {Date} 较晚的日期
*/
export function getLatestDate(date1, date2 = new Date()) {
  // 转换为Date对象
  const d1 = new Date(date1);
  const d2 = new Date(date2);
  // 验证输入
  if (Number.isNaN(d1) || Number.isNaN(d2)) {
      console.log('Invalid date format');
      return new Date();
  }
  // 使用compareAsc比较
  return compareAsc(d1, d2) === 1 ? d1 : d2;
};




/**
 * 計算不包含開始日期和結束日期當月的中間月份數
 * @param {string|Date} startDate - 開始日期
 * @param {string|Date} endDate - 結束日期
 * @returns {number} - 返回中間月份數
 */
export function getMiddleMonthsCount(startDate, endDate) {
  // 確保日期是有效的 Date 對象
  const start = typeof startDate === 'string' ? new Date(startDate) : startDate;
  const end = typeof endDate === 'string' ? new Date(endDate) : endDate;

  // 如果日期無效，返回 0
  if (Number.isNaN(start?.getTime()) || Number.isNaN(end?.getTime()) || start > end) {
    console.warn('Invalid date(s) provided to getMiddleMonthsCount.');
    return 0;
  }

  // 如果開始和結束日期在同一個月，則中間月份數為 0
  if (isSameMonth(start, end)) {
    return 0;
  }

  // 獲取開始日期的月份的最後一天和結束日期的月份的第一天
  const startMonthEnd = endOfMonth(start);
  const endMonthStart = startOfMonth(end);

  // 如果開始日期是該月的最後一天，或結束日期是該月的第一天，則中間月份數為 0
  if (isSameMonth(startMonthEnd, endMonthStart)) {
    return 0;
  }

  // 計算總月份數
  const totalMonths = differenceInMonths(endMonthStart, startMonthEnd);

  // 確保結果非負
  return Math.max(0, totalMonths);
};



/**
 * 判斷開始日期和結束日期是否為完整月份，並計算月份數量和天數
 * @param {string|Date} startDate - 開始日期
 * @param {string|Date} endDate - 結束日期
 * @returns {Object} - 返回包含月份數量和天數的對象 { months: number, days: number }
 */
export function calculateMonthsAndDays(startDate, endDate) {
  // 確保日期是有效的 Date 對象
  const start = typeof startDate === 'string' ? new Date(startDate) : startDate;
  const end = typeof endDate === 'string' ? new Date(endDate) : endDate;

  // 如果日期無效，返回 0
  if (Number.isNaN(start?.getTime()) || Number.isNaN(end?.getTime()) || start > end) {
    console.warn('Invalid date(s) provided to calculateMonthsAndDays.');
    return { months: 0, days: 0 };
  }

  // 如果開始和結束日期在同一個月
  if (isSameMonth(start, end)) {
    const isStartFullMonth = isSameDay(start, startOfMonth(start));
    const isEndFullMonth = isSameDay(end, endOfMonth(end));

    if (isStartFullMonth && isEndFullMonth) {
      // 同一月份且為完整月份
      return { months: 1, days: 0 };
    } 
    // 同一月份但不完整，計算天數
    const days = differenceInDays(end, start) + 1; // 包含開始和結束日期
    return { months: 0, days };
  }

  // 如果開始和結束日期不在同一個月
  let months = 0;
  let days = 0;

  // 判斷開始日期是否為完整月
  const isStartFullMonth = isSameDay(start, startOfMonth(start));
  if (isStartFullMonth) {
    // eslint-disable-next-line no-plusplus
    months += 1;
  } else {
    // 計算開始月份的剩餘天數
    const startMonthEnd = endOfMonth(start);
    // eslint-disable-next-line no-plusplus
    days += differenceInDays(startMonthEnd, start) + 1; // 包含開始日期
  }

  // 判斷結束日期是否為完整月
  const isEndFullMonth = isSameDay(end, endOfMonth(end));
  if (isEndFullMonth) {
    // eslint-disable-next-line no-plusplus
    months += 1;
  } else {
    // 計算結束月份的天數
    const endMonthStart = startOfMonth(end);
    // eslint-disable-next-line no-plusplus
    days += differenceInDays(end, endMonthStart) + 1; // 包含結束日期
  }

  return { months, days };
}



/**
* 计算两个日期之间的完整月数量及除完整月份外的剩余天数
* @param {string|Date} startDate - 开始日期（支持字符串如 '2025-05-23' 或 Date 对象）
* @param {string|Date} endDate - 结束日期（支持字符串如 '2025-06-30' 或 Date 对象）
* @returns {Object} 包含完整月数量和剩余天数 { fullMonths: number, remainingDays: number }
*/
export function getMonthsAndRemainingDays(startDate, endDate) {
  // 转换为 Date 对象
  const start = startDate instanceof Date ? startDate : new Date(startDate);
  const end = endDate instanceof Date ? endDate : new Date(endDate);

  // 验证日期有效性
  if (Number.isNaN(start) || Number.isNaN(end) || start > end) {
      return { fullMonths: 0, remainingDays: 0 };
  };

  const betweenMonths = getMiddleMonthsCount(startDate, endDate);

  const { months, days } = calculateMonthsAndDays(startDate, endDate);

  const fullMonths = betweenMonths + months;

  const remainingDays = days;
  
  return { fullMonths, remainingDays };
};



/**
 * 计算包含自然月的租金
 * @param startDate 开始日期
 * @param endDate 结束日期
 * @param monthlyRent 月租价格
 * @returns 计算后的总租金
 */
export function calculateRent(
  startDate,
  endDate,
  monthlyRent,
  demandNumber = 0
) {
  // 确保日期是Date对象
  const start = typeof startDate === 'string' ? new Date(startDate) : startDate;
  const end = typeof endDate === 'string' ? new Date(endDate) : endDate;


  // 如果日期無效，返回 0
  if (Number.isNaN(start?.getTime()) || Number.isNaN(end?.getTime())) {
    console.warn('Invalid date(s) provided to calculateRent.');
    return 0;
  };


  // 確保月租價格為有效數字
  if (typeof monthlyRent !== 'number' || monthlyRent <= 0) {
    console.warn('Invalid monthlyRent provided to calculateRent.');
    return 0;
  }
  
  // 计算完整自然月数量
  // const { fullMonths, remainingDays } = getFullMonthsAndRemainingDays(start, end);
  const { fullMonths, remainingDays } = getMonthsAndRemainingDays(start, end);
  
  
  // 计算日租金
  const dailyRate = monthlyRent / 30;
  
  // 计算基础总租金
  const baseRent = (fullMonths * monthlyRent) + (remainingDays * dailyRate);
  
  // 乘以需求数并返回结果
  return Math.round(baseRent * demandNumber);
};